import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import './VideoHistory.css';

interface ContentData {
  prompt: string;
  duration?: number;
  size?: string;
  image?: string;
}

interface HistoryItem {
  id: string;
  task_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'created';
  filename: string;
  create_time: any | string | number;
  update_time: string;
  completed_time?: string;
  duration: string;
  upload_url: string;
  thumbnail_url?: string;
  file_size?: string;
  content?: string;
  name?: string;
  parsedContent?: ContentData;
  service_type?: string;
}

// 暴露给父组件的方法接口
export interface VideoHistoryRef {
  refreshHistory: () => void;
}

// 解析 content 字符串的工具函数
const parseContent = (contentStr?: string): ContentData | null => {
  if (!contentStr) return null;
  try {
    return JSON.parse(contentStr);
  } catch (error) {
    console.error('Failed to parse content:', error);
    return null;
  }
};

// 👇 模拟你的 fetchUserHistory 函数（你可以替换成 import 的真实函数）
const fetchUserHistory = async (page: number = 1, limit: number = 20) => {
  const token = localStorage.getItem('glistar_token');
  if (!token) {
    return { data: [], total: 0, page: 1, limit };
  }

  // Debug mode
  const debugEmpty = localStorage.getItem('debug_empty_history') === 'true';
  if (debugEmpty) {
    console.log('🐛 Debug: returning empty history');
    return { data: [], total: 0, page: 1, limit };
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/user/history?page=${page}&limit=${limit}&type=2`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = await response.json();

    if (response.ok && result.status === 200) {  
      const { list = [], total = 0 } = result.data || {};
      // 解析每个 history item 的 content
      const parsedData = list.map((item: HistoryItem) => ({
        ...item,
        parsedContent: parseContent(item.content),
      }));

      return { data: parsedData, total, page, limit };
    } else {
      console.error('API error:', result.message || response.statusText);
      return { data: [], total: 0, page, limit };
    }
  } catch (error) {
    console.error('Network error:', error);
    return { data: [], total: 0, page, limit };
  }
};

const VideoHistory = forwardRef<VideoHistoryRef>((_,ref) => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const limit = 20; // 每页条数

  const [isPlaying, setIsPlaying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 暴露刷新方法给父组件
  useImperativeHandle(ref, () => ({
    refreshHistory: () => {
      console.log('Refreshing history...');
      // 重置分页并重新加载第一页
      setCurrentPage(1);
      loadHistory(1, true);
    }
  }));

  const handleDelete = async (taskId: string) => {
    // 删除视频的逻辑
    if (window.confirm('Are you sure you want to delete this video?')) {
      console.log('Delete video');
      setIsDeleting(true); 
      // TODO: 实际删除逻辑
    
      try {
        const token = localStorage.getItem('glistar_token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user/del-history`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            // 在这里传你要的参数
            taskid: taskId
          })
        });
        const result = await response.json();
        if (result.status === 200) {
          // 👇 删除成功后调用回调刷新历史记录
          loadHistory(1);
          setIsDeleting(false);
        } else {
          console.error('Failed to delete task:', response.statusText);
        }
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleDownload = (videoUrl: string) => {
    // 下载视频的逻辑
    console.log('Download video');
    const link = document.createElement('a');
    link.href = videoUrl ?? '';
    link.download = 'new-video.mp4'; // 👈 关键：提示浏览器这是下载
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadHistory = useCallback(async (page: number, isRefresh: boolean = false) => {
    setLoading(true);
    try {
      const res = await fetchUserHistory(page, limit);
      if (isRefresh || page === 1) {
        setHistoryItems(res.data);
      } else {
        setHistoryItems((prev) => [...prev, ...res.data]);
      }
      setHasMore(res.data.length === limit);
      setCurrentPage(page);
      setError(null);
    } catch (err) {
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

   // 初始加载第 1 页
  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);
  
  const handleLoadMore = () => {
    loadHistory(currentPage + 1);
  };

  return (
    <div className="video-history">
      <div className="history-header">
        <h3>History</h3>
      </div>

      <div className="history-content">
        {historyItems.length === 0 ? (
          <div className="empty-history">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <p>No videos generated yet</p>
            <span>Your video history will appear here</span>
          </div>
        ) : (
          <>
            <div className="history-list">
              {historyItems.map((item) => (
                <div className="video-container">
                  <div className="video-player">
                    {/* 已完成且有视频URL - 显示视频 */}
                    {item.status === 'completed' && item.upload_url && (
                      <video
                        src={item.upload_url}
                        controls
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}

                    {/* 处理中状态 或 已完成但无视频URL - 显示处理中图片 */}
                    {((item.status === 'processing' || item.status === 'created' || item.status === 'queued') ||
                      (item.status === 'completed' && !item.upload_url)) && (
                      <img 
                        src="https://cdn.glistar.ai/faceswappage.ai/video-pro.png" 
                        alt="PROCESSING"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}

                    {/* 失败状态 - 显示失败图片 */}
                    {item.status === 'failed' && (
                      <img 
                        src="https://cdn.glistar.ai/faceswappage.ai/video-fail.png" 
                        alt="FAILED"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                  </div>

                  <div className="video-description">
                    <div className="video-metadata">
                      <span className="metadata-value">{item.name}</span>
                    </div>
                    {item.parsedContent?.image && (
                      <img 
                        src={item.parsedContent?.image} 
                        alt='image'
                        className="video-image"
                        onError={(e) => {
                          // 图片加载失败时显示占位符
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="description-text">
                      <p>
                        {item.parsedContent?.prompt || 'No description available'}
                      </p>
                    </div>
                    <div className="video-btns">
                      {(item.status === 'completed' && item.upload_url) && (
                        <button className="action-btn" onClick={() => handleDownload(item.upload_url)} title="Download video">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                      <button className="delete-btn" onClick={() => handleDelete(item.id)} title="Delete video">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    
                  </div>
                </div>
                // <div
                //   key={item.id}
                //   className={`history-item ${selectedVideo === item.id ? 'selected' : ''}`}
                //   onClick={() => handleVideoClick(item)}
                // >
                //   <div className="item-thumbnail">
                //     <img src={item.thumbnail_url} alt={item.filename} />
                //     <div className="play-overlay">
                //       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                //         <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                //       </svg>
                //     </div>
                //   </div>
                // </div>
              ))}
            </div>

            {hasMore && !loading && (
                <div className="load-more">
                  <button onClick={handleLoadMore} disabled={loading}>
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
          </>
        )}

        {loading && historyItems.length > 0 && (
          <div className="loading-more">Loading more...</div>
        )}
      </div>
    </div>
  );
});

export default VideoHistory;

